import { getMvpStream } from "../lib/api";
import sanitizeMessages from "../utils/sanitizeMessages";
import type { Dispatch, SetStateAction } from "react";

type UseDeploymentHandlerParams = {
  ideas: any[];
  updateIdea: (id: string, updates: any) => void;
  setDeployLogs: Dispatch<SetStateAction<string[]>>;
};

export function useDeploymentHandler({
  ideas,
  updateIdea,
  setDeployLogs,
}: UseDeploymentHandlerParams) {
  const simulateStreamingLog = (ideaId: string, line: string) => {
    updateIdea(ideaId, (prev: any) => ({
      ...prev,
      messages: [
        ...(prev.messages || []),
        { role: "assistant", content: line },
      ],
    }));
  };

  const handleConfirmBuild = async (id: string) => {
    const idea = ideas.find((i) => i.id === id);
    if (!idea || !idea.takeaways?.branding || !idea.messages?.length) {
      updateIdea(id, {
        deployError: "Missing ideaId, branding, or messages",
      });
      return;
    }

    updateIdea(id, { deploying: true });
    setDeployLogs([]);

    let messageAccumulator = [...idea.messages];

    // Add MVP placeholder
    const placeholderMsg = {
      role: "assistant" as const,
      content: "🛠️ Building your MVP...",
    };
    messageAccumulator = [...messageAccumulator, placeholderMsg];
    updateIdea(id, { messages: messageAccumulator });

    let firstLogSeen = false;

    const appendLog = (line: string) => {
      let parsed: any = null;

      try {
        parsed = JSON.parse(line);
      } catch {
        // not JSON — treat as plain string
      }

      let displayText: string | null = null;

      if (parsed) {
        if (parsed.message) {
          displayText = parsed.message;
        } else if (parsed.pagesUrl) {
          displayText = `🌍 Site: ${parsed.pagesUrl}`;
        } else if (parsed.repoUrl) {
          displayText = `📁 Repo: ${parsed.repoUrl}`;
        } else if (parsed.error) {
          displayText = `❌ ${parsed.error}`;
        }
      } else {
        displayText = line;
      }

      if (displayText) {
        messageAccumulator = [
          ...messageAccumulator,
          { role: "assistant", content: displayText },
        ];
        simulateStreamingLog(id, displayText);
      }
    };

    try {
      await getMvpStream(
        idea.id,
        idea.takeaways.branding,
        sanitizeMessages(idea.messages),
        // onLog
        (log) => {
          setDeployLogs((prev) => [...prev, log]);

          if (!firstLogSeen) {
            updateIdea(id, (prev: any) => ({
              ...prev,
              messages: prev.messages.filter(
                (m: any) => m.content !== "🛠️ Building your MVP..."
              ),
            }));
            firstLogSeen = true;
          }

          appendLog(log);
        },
        // onDone
        (data) => {
          const { pagesUrl, repoUrl, plan } = data || {};

          if (pagesUrl) {
            const finalLine =
              `✅ Deployment successful! Your site is live at ${pagesUrl}` +
              (repoUrl ? `\nGitHub repo: ${repoUrl}` : "");
            appendLog(finalLine);
            updateIdea(id, {
              deploying: false,
              deployed: true,
              repoUrl: repoUrl || "",
              pagesUrl,
            });
          } else if (plan) {
            appendLog("✅ Here’s the MVP build plan generated by the agent:\n\n" + plan);
            updateIdea(id, {
              deploying: false,
              takeaways: {
                ...idea.takeaways,
                finalPlan: plan,
              },
            });
          } else {
            appendLog("⚠️ Deployment completed but no URL or plan was returned.");
            updateIdea(id, { deploying: false });
          }
        },
        // onError
        (errMsg) => {
          appendLog(`❌ Deployment failed: ${errMsg}`);
          updateIdea(id, {
            deploying: false,
            deployError: errMsg,
          });
        }
      );
    } catch (err: any) {
      const errMsg = err instanceof Error ? err.message : String(err);
      appendLog(`❌ Deployment failed: ${errMsg}`);
      updateIdea(id, {
        deploying: false,
        deployError: errMsg,
      });
    }
  };

  return handleConfirmBuild;
}